import { useState, useEffect, useMemo, useRef } from "react";
import { Modal, Tabs, Button } from "antd";
import { useSelector } from "react-redux";
import { fetchHistory } from "src/actions/historyTrial";
import { WarningOutlined, CheckOutlined } from "@ant-design/icons";
import { FootnoteDetails, FootnotePreview } from "./components";
import {
  FOOTNOTE_GROUP,
  FOOTNOTE_TYPE_CHECK,
  SOFT_SAVE_ID_PREFIX,
} from "src/DigitalProtocolModule/constants";
import { useFootnoteHooks } from "src/DigitalProtocolModule/hooks";
import { uniqueArray } from "src/utils/common-function";
import styles from "./index.module.scss";
import "./index.scss";

const { TabPane } = Tabs;
const DEFAULT_ACTIVE_TAB = "1";

interface AddFootnoteModalProps {
  isOpen: Digital_Protocol_SoA_Table_Data.OpenFootnoteType;
  onCancel: () => void;
}

export function AddFootnoteModal({ isOpen, onCancel }: AddFootnoteModalProps) {
  // get footnote config
  const footnoteConfigState = useSelector(fetchHistory).data.dpFootnoteConfig;

  const detailsRef = useRef<any>();

  const { getFootnoteStateMergedWithSoftSave, doFootnoteSoftUpsert } =
    useFootnoteHooks();

  const [footnoteMergedDataState] = useState(
    getFootnoteStateMergedWithSoftSave()
  );
  const [activeFootnoteType, setActiveFootnoteType] =
    useState<DPFootnote.FootnoteType>();
  const [activeTab, setActiveTab] = useState(DEFAULT_ACTIVE_TAB);
  const [footnoteDataListForCurrentModal, setFootnoteDataListForCurrentModal] =
    useState<DPFootnote.FootnoteData[]>([]);
  const [footnoteTypesList, setFootnoteTypesList] = useState<
    DPFootnote.FootnoteType[]
  >([]);
  const [jsonData, setJsonData] = useState<any>({});
  const [currentFootnote, setCurrentFootnote] =
    useState<DPFootnote.FootnoteData | null>(null);

  const footnoteComponentType = useMemo(() => {
    let type: string = "";
    switch (activeFootnoteType?.footnote_type.toLowerCase()) {
      case "activity only applicable to certain patient population":
      case "visit is only applicable to a certain patient population":
      case "period is only applicable to a certain patient population":
        type = FOOTNOTE_TYPE_CHECK.IS_PATIENT_POPULATION;
        break;
      case "activity has sub-activities":
      case "activity is continuously monitored for this visit":
        type = FOOTNOTE_TYPE_CHECK.IS_SUB_ACTIVITY;
        break;
      case "activity has a different workflow for certain patients":
        type = FOOTNOTE_TYPE_CHECK.IS_DIFFERENT_WORKFLOW;
        break;
      case "activity repeated in certain cases":
        type = FOOTNOTE_TYPE_CHECK.IS_REPEAT_IN_CERTAIN_CASE;
        break;
      case "activity is continuously monitored":
        type =
          activeFootnoteType.footnote_group === FOOTNOTE_GROUP.PERIOD
            ? FOOTNOTE_TYPE_CHECK.IS_SUB_ACTIVITY
            : FOOTNOTE_TYPE_CHECK.IS_CONTINUOUSLY_MONITORED;
        break;
      case "activity uses multiple modalities":
        type = FOOTNOTE_TYPE_CHECK.IS_MULTIPLE_MODALITIES;
        break;
      case "free text footnote":
        type = FOOTNOTE_TYPE_CHECK.IS_FREE_TEXT;
        break;
      default:
        break;
    }
    return type;
  }, [activeFootnoteType]);

  const handleJsonDataChange = (
    fields: string | string[],
    values: number | string | any[],
    previewPre: string,
    previewText: string
  ) => {
    let tmpJsonData = { ...jsonData };
    if (
      fields === "different_workflow_type_select" ||
      fields.includes("different_workflow_type_select")
    ) {
      delete tmpJsonData.different_workflow_1_text;
      delete tmpJsonData.different_workflow_1_select;
      delete tmpJsonData.different_workflow_2_text;
      delete tmpJsonData.different_workflow_2_select;
    }

    if (typeof fields === "string") {
      tmpJsonData = {
        ...tmpJsonData,
        [fields]: values,
        previewPre,
        footnotePreivew: previewText,
        footnoteType: activeFootnoteType.footnote_type_id,
      };
    } else {
      fields.forEach((field: string, index: number) => {
        tmpJsonData = {
          ...tmpJsonData,
          [field]: values[index],
        };
      });
      tmpJsonData = {
        ...tmpJsonData,
        previewPre,
        footnotePreivew: previewText,
        footnoteType: activeFootnoteType.footnote_type_id,
      };
    }

    setJsonData(tmpJsonData);
  };

  const handleSwitchFootnoteType = (noteTypeItem: DPFootnote.FootnoteType) => {
    // if current footnote was exist or is empty
    if (
      Boolean(jsonData?.footnotePreivew?.trim()) ||
      footnoteTypesList.some(
        (item) =>
          item.checked &&
          item.footnote_type_id === activeFootnoteType?.footnote_type_id
      )
    ) {
      const isValid = doValidation();
      if (!isValid) {
        return;
      }
      const footnoteItem = {
        footnote_id:
          currentFootnote?.footnote_id ||
          `${SOFT_SAVE_ID_PREFIX}${new Date().getTime()}`,
        footnote_details_json: JSON.stringify(jsonData),
        footnote_preview_text: jsonData?.footnotePreivew,
        footnote_group_id: activeFootnoteType.footnote_group_id,
        footnote_group: activeFootnoteType.footnote_group,
        footnote_type_id: activeFootnoteType.footnote_type_id,
        trial_uuid: isOpen.trial_uuid,
        table_id: isOpen.table_id,
        dprot_id: isOpen.dprot_id,
        activity_id: isOpen?.activity?.activity_id || null,
        period_id: isOpen?.period_id || null,
        visit: isOpen?.visit_number || null,
        week: isOpen?.week || null,
        day: isOpen?.day || null,
      };
      setFootnoteDataListForCurrentModal(
        uniqueArray(
          [footnoteItem, ...footnoteDataListForCurrentModal],
          "footnote_id"
        )
      );
      const isNeedToChangeCheckState = footnoteTypesList.find(
        (item) =>
          item.footnote_type_id === footnoteItem.footnote_type_id &&
          !item.checked
      );
      if (isNeedToChangeCheckState) {
        setFootnoteTypesList(
          footnoteTypesList.map((item) => ({
            ...item,
            checked:
              item.footnote_type_id === footnoteItem.footnote_type_id ||
              item.checked,
          }))
        );
      }
    }
    const tmpCurrentFootnote =
      footnoteDataListForCurrentModal.find(
        (item) => item.footnote_type_id === noteTypeItem.footnote_type_id
      ) || null;
    setCurrentFootnote(tmpCurrentFootnote);
    setJsonData(
      tmpCurrentFootnote
        ? JSON.parse(tmpCurrentFootnote?.footnote_details_json)
        : {}
    );
    setActiveFootnoteType(noteTypeItem);
    setActiveTab(DEFAULT_ACTIVE_TAB);
  };

  const handleTabChange = (activeKey) => {
    setActiveTab(activeKey);
  };

  const doValidation = () => {
    const isValid = detailsRef.current.doValidation
      ? detailsRef.current.doValidation()
      : true;
    return isValid;
  };

  const handleSubmit = () => {
    const isValid = doValidation();
    if (!isValid) {
      return;
    }
    const softSavePayload: DPFootnote.PayloadType = {
      footnote_id:
        currentFootnote?.footnote_id ||
        `${SOFT_SAVE_ID_PREFIX}${new Date().getTime()}`,
      footnote_details_json: JSON.stringify(jsonData),
      footnote_preview_text: jsonData?.footnotePreivew,
      footnote_group_id: activeFootnoteType.footnote_group_id,
      footnote_group: activeFootnoteType.footnote_group,
      footnote_type_id: activeFootnoteType.footnote_type_id,
      trial_uuid: isOpen.trial_uuid,
      table_id: isOpen.table_id,
      dprot_id: isOpen.dprot_id,
      activity_id: isOpen?.activity?.activity_id || null,
      period_id: isOpen?.period_id || null,
      visit: isOpen?.visit_number || null,
      week: isOpen?.week || null,
      day: isOpen?.day || null,
    };
    const allSoftSavePayloadList = uniqueArray(
      [softSavePayload, ...footnoteDataListForCurrentModal],
      "footnote_id"
    );
    // soft save footnote
    doFootnoteSoftUpsert(allSoftSavePayloadList);
    onCancel();
  };

  useEffect(() => {
    /** checking footnote type logic:
     *           table id | period id | visit | week | day | activity id
     * cell:         Y           Y         Y       Y     Y        Y
     * period:       Y           Y         N       N     N        N
     * visit:        Y           Y         Y       N     N        N
     * activity:     Y           N         N       N     N        Y
     */
    // check and filter out any footnote data matches current group and activity
    let tmpFootnoteDataListForCurrentModal: DPFootnote.FootnoteData[] = [];
    switch (isOpen.footnote_group) {
      case FOOTNOTE_GROUP.ACTIVITY:
        tmpFootnoteDataListForCurrentModal = footnoteMergedDataState?.filter(
          (item: DPFootnote.FootnoteData) =>
            item?.footnote_group === isOpen?.footnote_group &&
            item?.activity_id === isOpen?.activity?.activity_id
        );
        break;
      case FOOTNOTE_GROUP.VISIT:
        tmpFootnoteDataListForCurrentModal = footnoteMergedDataState?.filter(
          (item: DPFootnote.FootnoteData) =>
            item?.footnote_group === isOpen?.footnote_group &&
            item?.visit === isOpen?.visit_number
        );
        break;
      case FOOTNOTE_GROUP.CELL:
        tmpFootnoteDataListForCurrentModal = footnoteMergedDataState?.filter(
          (item: DPFootnote.FootnoteData) =>
            item?.footnote_group === isOpen?.footnote_group &&
            item?.activity_id === isOpen?.activity?.activity_id &&
            item?.period_id === isOpen?.period_id &&
            item?.visit === isOpen?.visit_number
        );
        break;
      case FOOTNOTE_GROUP.PERIOD:
        tmpFootnoteDataListForCurrentModal = footnoteMergedDataState?.filter(
          (item: DPFootnote.FootnoteData) =>
            item?.footnote_group === isOpen?.footnote_group &&
            item?.period_id === isOpen?.period_id &&
            !item?.activity_id
        );
        break;
      default:
        break;
    }
    setFootnoteDataListForCurrentModal(tmpFootnoteDataListForCurrentModal);

    if (footnoteConfigState?.footnoteTypeList?.length > 0) {
      let currentFootnoteTypes = footnoteConfigState?.footnoteTypeList
        ?.filter(
          (item: DPFootnote.FootnoteData) =>
            item.footnote_group === isOpen.footnote_group
        )
        .map((item: DPFootnote.FootnoteData) => ({
          ...item,
          checked: tmpFootnoteDataListForCurrentModal
            .map((item) => item.footnote_type_id)
            .includes(item.footnote_type_id),
        }));
      setFootnoteTypesList(currentFootnoteTypes);
    } else {
      setFootnoteTypesList([]);
    }
  }, [footnoteConfigState, footnoteMergedDataState]);

  useEffect(() => {
    if (isOpen.visible) {
      if (isOpen.footnote_type_id && !activeFootnoteType) {
        const initFootnoteType = footnoteTypesList.find(
          (item: DPFootnote.FootnoteType) =>
            item.footnote_type_id === isOpen.footnote_type_id
        );
        if (initFootnoteType) {
          handleSwitchFootnoteType(initFootnoteType);
        }
      } else if (!activeFootnoteType) {
        // set to the first menu by default
        handleSwitchFootnoteType(footnoteTypesList[0]);
      }
    }
  }, [isOpen, footnoteTypesList, activeFootnoteType]);

  return (
    <Modal
      visible={isOpen.visible}
      onCancel={onCancel}
      title="Add a Footnote"
      footer={[
        <Button key="back" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          style={{
            backgroundColor: "#ca4a04",
            color: "#fff",
            borderColor: "#ca4a04",
          }}
          onClick={handleSubmit}
          disabled={!activeFootnoteType}
        >
          Submit
        </Button>,
      ]}
      wrapClassName="add-footnote-modal-root"
    >
      <div className={styles.root}>
        <div className={styles.left}>
          <div className={styles.pageTitle}>Select Footnote Type</div>
          <div className={styles.pageSubTitle}>
            Select a footnote type here to add / edit details on the right
          </div>
          <div className={styles.typeList}>
            {footnoteTypesList.map((noteTypeItem) => (
              <div
                className={`${styles.listItem} ${
                  noteTypeItem.checked ? styles.checked : ""
                } ${
                  activeFootnoteType?.footnote_type_id ===
                  noteTypeItem.footnote_type_id
                    ? styles.active
                    : ""
                }`}
                onClick={() => handleSwitchFootnoteType(noteTypeItem)}
                key={noteTypeItem.footnote_type_id}
              >
                <span>{noteTypeItem.footnote_type}</span>
                {noteTypeItem.checked ? (
                  <CheckOutlined
                    style={{ fontSize: 16 }}
                    className={noteTypeItem.checked ? styles.checkedIcon : ""}
                  />
                ) : (
                  <WarningOutlined style={{ fontSize: 16 }} />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className={styles.divideLine} />

        {activeFootnoteType && (
          <div className={styles.right}>
            <Tabs activeKey={activeTab} onChange={handleTabChange}>
              <TabPane tab={<>Details</>} key="1">
                <FootnoteDetails
                  ref={detailsRef}
                  currentFootnoteType={activeFootnoteType}
                  activity={isOpen?.activity}
                  jsonData={jsonData}
                  footnoteComponentType={footnoteComponentType}
                  footnoteGroupName={isOpen.footnote_group}
                  handleJsonDataChange={handleJsonDataChange}
                  visitNumber={isOpen?.visit_number}
                  period={isOpen?.period}
                />
              </TabPane>

              <TabPane tab={<>Footnote Preview</>} key="2">
                <FootnotePreview
                  previewText={jsonData?.footnotePreivew || ""}
                />
              </TabPane>
            </Tabs>
          </div>
        )}
      </div>
    </Modal>
  );
}
